import {
    CRM_DOCUMENT_FIELD_LABELS as L,
    CRM_DOCUMENT_FILE_FIELDS,
    CrmDocumentCardTemplate,
    crmDocumentFileFields,
} from '@/Containers/CrmSection/Components/Documents'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { APPLICATION_DOCUMENT_REST_URL } from '../Contracts/IApplication'

import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Word і PDF — одним кроком у збережений документ (`SPEC-008` §5.7.1). */
const FILE_FIELDS = crmDocumentFileFields(APPLICATION_DOCUMENT_REST_URL)

/**
 * Поля, яких бек від форми не приймає: службові, назва (з номера й дати),
 * файли (окремими запитами) і заявка після створення.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
    ...CRM_DOCUMENT_FILE_FIELDS,
] as const

const prepareDocumentPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['application']
}

/**
 * Документи заявки — «Друковані форми» (`SPEC-021` §8.7): колонки й картка —
 * спільні CRM (`CrmDocumentsTab`, `CrmDocumentCardTemplate`), як у клієнта.
 *
 * Власного реєстру немає; створюють лише з картки заявки (`CREATED_FROM_PARENT`).
 * Права — правами заявки (`ApplicationRule`: документ веде той, хто змінює
 * заявку). «Тип документа» — лише типи провайдера «Заявка» з
 * `isUseInApplication` (бек перевіряє те саме). Генерація з шаблону — меню
 * вкладки (`CRM_DOCUMENT_GENERATION.application`, `SPEC-025` §3.1.1);
 * Collabora — `CrmDocumentCardTemplate` через
 * `/services/application_documents/{uuid}/edit_session`.
 *
 * Word і PDF вантажаться одним кроком у збережений документ — ресурс у
 * `ONE_STEP_UPLOADS` вартового завантажень.
 */
export const ApplicationDocumentResource: IResourceConfig = {
    title: 'Документи заявки',
    fields: {
        documentType: {
            label: L.documentType,
            type: 'select',
            resource: 'Setting.ListDocumentType',
            isRequired: true,
            inputProps: { filter: { isUseInApplication: true, provider: 'application' } },
        },
        title: readOnly({
            label: L.title,
        }),
        number: {
            label: L.number,
            inputProps: { maxLength: 150 },
        },
        date: {
            label: L.date,
            type: 'date',
            view: renderDateView,
        },
        wordFile: {
            ...FILE_FIELDS.wordFile,
            type: 'file',
        },
        pdfFile: {
            ...FILE_FIELDS.pdfFile,
            type: 'file',
        },
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                ApplicationDocumentPayload: prepareDocumentPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: CrmDocumentCardTemplate },
            edit: { template: CrmDocumentCardTemplate },
            create: { template: CrmDocumentCardTemplate },
        },
    },
    drawer: {
        width: 600,
    },
    restUrl: APPLICATION_DOCUMENT_REST_URL,
}
